import {
  LitElement,
  classMap,
  createRef,
  css,
  html,
  nothing,
  styleMap,
  ref,
  unsafeHTML,
} from '@lit'

import * as paths from '@nova/paths'
import styles from '@nova/styles'
import { formatMoney, handleize, t } from '@nova/utils'

import { addToCart } from '@nova/utils'

const quickShopState = {
  initial: 'SHOW_OPTION_SELECTOR',
  submit: 'SHOW_ADD_TO_CART',
  loading: 'SHOW_LOADING',
  confirm: 'SHOW_CONFIRMATION',
  error: 'ERROR',
}

class NovaProductCard extends LitElement {
  static properties = {
    url: {
      type: String,
    },
    available: {
      type: Boolean,
    },
    productTitle: {
      type: String,
      attribute: 'product-title',
    },
    subtitle: {
      type: String,
    },
    image: {
      type: String,
      attribute: 'image',
    },
    imageAlt: {
      type: String,
      attribute: 'image-alt',
    },
    secondaryImage: {
      type: String,
      attribute: 'secondary-image',
    },
    secondaryImageAlt: {
      type: String,
      attribute: 'secondary-image-alt',
    },
    compareAtPrice: {
      type: String,
      attribute: 'compare-at-price',
    },
    swatches: {
      type: Array,
    },
    swatchesData: {
      type: Array,
    },
    sizes: {
      type: Array,
    },
    price: {
      type: String,
    },
    vendor: {
      type: String,
    },
    variants: {
      type: Array,
    },
    badges: {
      type: Array,
    },
    activeIndex: {
      type: Number,
    },
    selectedIndex: {
      type: String,
      attribute: 'selected-index',
    },
    options: {
      type: Array,
    },
    hideRatings: {
      attribute: 'hide-ratings',
    },
    showWishlist: {
      type: Boolean,
      attribute: 'show-wishlist'
    },
    hideSwatches: {
      state: true,
    },
    isHovered: {
      state: true,
    },
    isGiftCollection: {
      type: Boolean,
      attribute: 'is-gift-collection'
    },
    isMobile: {
      state: true,
    },
    isHoverableDevice: {
      type: Boolean,
      state: true,
    },
    slidesForCarousel: {
      type: Number,
      state: true,
    },
    quickShopVariant: {
      type: Object,
      state: true,
    },
    status: {
      type: String,
      state: true,
    },
    class: {
      type: String,
      attribute: 'class',
    },
    callOut: {
      type: String,
      attribute: 'data-call-out',
    },
    collectionHandle: {
      type: String,
      attribute: 'collection-handle',
    },
    dataNewBadgeText: {
      type: String,
      attribute: 'data-new-badge-text',
    },
    callOutColor: {
      type: String,
      attribute: 'call-out-color',
    },
    mode: {
      type: String,
      attribute: 'data-mode',
    },
    variantUrls: {
      type: Object
    },
    activeVariantId: {
      type: String,
      state: true
    },
    activeVariantUrl: {
      type: String,
      state: true
    },
    isGiftCard: {
      type: Boolean,
      attribute: 'is-gift-card',
    },
    preOrderLabel: {
      type: String,
      attribute: 'pre-order-label',
    }
  }

  $productCardContentRef = createRef()
  $clubhouseBadgeRef = createRef()

  sanitizeString(string) {
    return string
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
  }

  get activeTitle() {
    return this.swatchVariants[this.activeIndex]?.swatchTitle || false
  }

  get activeId() {
    return this.swatchVariants[this.activeIndex]?.id || this.id
  }

  get activePrice() {
    if (this.quickShopVariant) return formatMoney(this.quickShopVariant.price)

    if (!this.swatchVariants.length) return formatMoney(this.price)

    return formatMoney(this.swatchVariants[this.activeIndex]?.price).replace('.00', '')
  }

  get activeAvailable() {
    if (this.quickShopVariant) return this.quickShopVariant.available

    if (this.swatchVariants[this.activeIndex]) {
      return this.swatchVariants[this.activeIndex]?.available
    }
    return this.available
  }

  get giftImage() {
    if (!this.isGiftCollection) return ''
    const images = this.swatchesData?.[this.activeIndex]?.images || []
    const gift = images.map(([src, attached_to_variant, alt]) => ({src, attached_to_variant, alt}))
      .find(({alt}) => alt?.includes('gift'))
    return gift?.src || ''
  }

  get firstImage() {
    const images = this.swatchesData?.[this.activeIndex]?.images || []
    const swatchProductImages = images.filter((item) => !item[1])
    return this.swatchVariants[this.activeIndex]?.featured_image?.src ||
      (swatchProductImages && swatchProductImages.length > 0 && swatchProductImages[0][0]) ||
      this.image ||
      ''
  }

  get activeImage() {
    let image = this.giftImage || this.firstImage

    const isSmall = image
      .split('_')
      .some(
        (fragment) => fragment.includes('x480') || fragment.includes('480x'),
      )

    if (!isSmall) {
      const fragments = image.split('.')
      fragments[fragments.length - 2] = `${
        fragments[fragments.length - 2]
      }_480x`
      image = fragments.join('.')
    }

    return `${image}&width=480&height=640`
  }

  get activeImageAlt() {
    const images = this.swatchesData?.[this.activeIndex]?.images || []

    if (this.giftImage) {
      const gift = images
        .map(([src, attached_to_variant, alt]) => ({ src, attached_to_variant, alt }))
        .find(({ alt }) => alt?.includes('gift'))
      return gift?.alt || this.productTitle || this.imageAlt || ''
    }

    const featured = this.swatchVariants[this.activeIndex]?.featured_image
    if (featured?.src) {
      return featured.alt || this.productTitle || this.imageAlt || ''
    }

    const swatchProductImages = images.filter((item) => !item[1])
    if (swatchProductImages?.length > 0) {
      return swatchProductImages[0][2] || this.productTitle || this.imageAlt || ''
    }

    return this.imageAlt || this.productTitle || ''
  }

  get activeSecondaryImage() {
    // non-attached images to variant
    const images = this.swatchesData?.[this.activeIndex]?.images || []
    const swatchProductImages = images.filter((item) => !item?.[1]) || []
    let secondaryProductImage = swatchProductImages?.length > 1 ? swatchProductImages[1]?.[0]: ''
    if (this.swatchVariants[this.activeIndex]?.featured_image?.src) {
      secondaryProductImage = swatchProductImages?.length ? swatchProductImages[0][0]: ''
    }
    let image =
      (this.giftImage && this.firstImage) ||
      secondaryProductImage ||
      ''
    if(!image) return ''
    const isSmall = image
      .split('_')
      .some(
        (fragment) => fragment.includes('x480') || fragment.includes('480x'),
      )
    if (!isSmall) {
      const fragments = image.split('.')
      fragments[fragments.length - 2] = `${
        fragments[fragments.length - 2]
      }_480x`
      image = fragments.join('.')
    }

    if (this.mode === 'featured-product' && this.isMobile) {
      this.handleFeaturedProductMobileVariantSwitch(image)
    }

    return `${image}&width=480&height=640`
  }

  get activeSecondaryImageAlt() {
    if (!this.activeSecondaryImage) {
      return this.secondaryImageAlt || this.productTitle || ''
    }

    const images = this.swatchesData?.[this.activeIndex]?.images || []
    const swatchProductImages = images.filter((item) => !item?.[1]) || []

    if (this.giftImage && this.firstImage) {
      const featured = this.swatchVariants[this.activeIndex]?.featured_image
      if (featured?.src) {
        return featured.alt || this.productTitle || this.secondaryImageAlt || ''
      }
      return (
        swatchProductImages[0]?.[2] ||
        this.productTitle ||
        this.secondaryImageAlt ||
        ''
      )
    }

    if (this.swatchVariants[this.activeIndex]?.featured_image?.src) {
      return (
        swatchProductImages[0]?.[2] ||
        this.productTitle ||
        this.secondaryImageAlt ||
        ''
      )
    }

    if (swatchProductImages?.length > 1) {
      return (
        swatchProductImages[1]?.[2] ||
        this.productTitle ||
        this.secondaryImageAlt ||
        ''
      )
    }

    return this.secondaryImageAlt || this.productTitle || ''
  }

  get hasHoverVideo() {
    return Boolean(this.activeHoverVideo)
  }

  get activeHoverVideo() {
    return this.swatchesData?.[this.activeIndex]?.video || null
  }

  get hasSecondaryMedia() {
    return (
      (this.hasHoverVideo && this.isHoverableDevice) ||
      Boolean(this.activeSecondaryImage)
    )
  }

  get activeUrl() {
    const id = this.quickShopVariant
      ? this.quickShopVariant.id
      : this.swatchVariants[this.activeIndex]?.id

    if (!id) return this.getCollectionAwareUrl(this.url)

    if (this.activeVariantUrl && String(this.activeVariantId) === String(id)) {
      return this.getCollectionAwareUrl(this.activeVariantUrl)
    }

    return this.getVariantUrlById(id)
  }

  getCollectionAwareUrl(url) {
    if (!url || !this.collectionHandle) return url

    if (url.includes('/collections/')) return url

    const baseUrl = url.startsWith('/') ? url : `/${url}`
    return `/collections/${this.collectionHandle}${baseUrl}`
  }

  getVariantUrlById(id) {
    const key = String(id)
    const map = this.variantUrls || {}
    if (map?.[key]) return this.getCollectionAwareUrl(map[key])

    const v = (this.variants || []).find(x => String(x.id) === key)
    if (v?.product_url) {
      const variantUrl = `${v.product_url}${v.product_url.includes('?') ? '&' : '?'}variant=${key}`
      return this.getCollectionAwareUrl(variantUrl)
    }

    return this.url ? this.getCollectionAwareUrl(`${this.url}?variant=${key}`) : ''
  }

  get allVariantsBySwatch() {
    const swatch = this.swatchVariants[this.activeIndex]?.swatchTitle
    return Array.from(this.variants).filter((variant) => variant.options?.includes(swatch))
  }

  get activeCompareAtPrice() {
    if (this.variants?.length > 1) {
      const activeVariant =
        this.quickShopVariant || this.swatchVariants[this.activeIndex]
      const _activeCompareAtPrice = Number.parseFloat(
        activeVariant?.compare_at_price,
      )
      const _activePrice = Number.parseFloat(activeVariant?.price)

      if (!_activeCompareAtPrice) return false

      if (_activeCompareAtPrice > _activePrice) {
        return formatMoney(
          this.swatchVariants[this.activeIndex]?.compare_at_price,
        ).replace('.00', '')
      }
    } else {
      if (!this.compareAtPrice) return false

      if (this.compareAtPrice > this.price)
        return formatMoney(this.compareAtPrice).replace('.00', '')
    }

    return false
  }

  get hiddenBadgeSet() {
    const raw = this.getAttribute('hide-badges')
    if (!raw || raw === 'null') return new Set()

    try {
      const decoded = this.decodeHtmlEntities(String(raw))
      const arr = JSON.parse(decoded)
      return new Set(
        arr.map(i => i?.label)
          .filter(Boolean)
          .map(l => this.sanitizeString(l))
      )
    } catch (e) {
      console.error('Failed to parse hide-badges:', e)
      return new Set()
    }
  }

  decodeHtmlEntities(str) {
    const textarea = document.createElement('textarea')
    textarea.innerHTML = str
    return textarea.value
  }

  constructor() {
    super()
    this.activeIndex = 0
    this.variants = []
    this.swatches = []
    this.swatchesData = []
    this.options = []
    this.swatchVariants = []
    this.swatchesPosition = 0
    this.swatchesPerSlide = 5
    this.activeSwatchPage = 0
    this.slidesForCarousel = 1
    this.isHovered = false
    this.isMobile = false
    this.status = quickShopState.initial
    this.showAddButton = false
    this.hideRatings = window.Shopify.shop.includes('wholesale') // Hide ratings by default on wholesale store
    this.callOut = ''
    this.activeColor = ''
    this.inventoryQtys = []
    this.activeInventoryQty = 1
    this.variantUrls = {}
    this.preOrderLabel = ''
    this.isHoverableDevice = window.matchMedia(
      '(hover: hover) and (pointer: fine)'
    ).matches
  }

  initVariables() {
    const script = this.querySelector('[type="application/json"]')
    if (!script) {
      console.warn('No <script> element with type="application/json" found. Product data could not be initialized.')
      return
    }
    const scriptContent = script.textContent?.replace('}, ]', '}]').replace('},]', '}]').replace('],]', ']]')
    const scriptData = JSON.parse(scriptContent || '{}')
    if (scriptData.variants) this.variants = scriptData.variants
    if (scriptData.swatches) this.swatches = scriptData.swatches
    if (scriptData.swatchesData) this.swatchesData = scriptData.swatchesData
    if (scriptData.sizes) this.sizes = scriptData.sizes
    if (scriptData.inventory_qtys) this.inventoryQtys = scriptData.inventory_qtys
    if (scriptData.variantUrls) this.variantUrls = scriptData.variantUrls
  }

  connectedCallback() {
    super.connectedCallback()
    this.initVariables()
    window.addEventListener('nova:resize-width', this.handleResize)

    this.addEventListener('swatch-selected', this.handleSwatchSelected)
  }

  disconnectedCallback() {
    window.removeEventListener('nova:resize-width', this.handleResize)
  }

  async firstUpdated() {
    this.initVariables()
    this.setSwatchVariants()

    const initId = this.swatchVariants[this.activeIndex]?.id
    if (initId) {
      this.activeVariantId = String(initId)
      this.activeVariantUrl = this.getVariantUrlById(initId)
    }

    if (this.options?.length) {
      this.setActiveByVariantId(this.id, true)
    }

    this.handleResize()
    this._updateClubhouseBadgePosition()
  }

  _updateClubhouseBadgePosition() {
    if (this.isMobile) return

    requestAnimationFrame(() => {
      const badge = this.$clubhouseBadgeRef.value
      const quickShop = this.shadowRoot?.querySelector('nova-product-card-quick-shop')

      if (badge && quickShop) {
        const quickShopHeight = quickShop.offsetHeight
        if (quickShopHeight > 0) {
          badge.style.bottom = `${quickShopHeight + 10}px`
        }
      }
    })
  }

  updated(changedProperties) {
    this.productTitle = this.productTitle.includes(' | ')
      ? this.productTitle.split(' | ')[0]
      : this.productTitle

    if (changedProperties.has('variants')) {
      this.setSwatchVariants()
    }

    const propsTriggeringUpdate = ['activeIndex', 'activeImage', 'activeSecondaryImage', 'swatches', 'sizes', 'isMobile']

    if (propsTriggeringUpdate.some(prop => changedProperties.has(prop))) {
      this.requestUpdate()
    }

    if (changedProperties.has('selectedIndex') && Number.parseInt(this.selectedIndex) > 0) {
      this.activeIndex = Number.parseInt(this.selectedIndex)
      this.activeColor = this.swatches[this.activeIndex]
    }

    if (changedProperties.has('isHovered') || changedProperties.has('activeIndex')) {
      this._updateClubhouseBadgePosition()
    }
  }

  handleOptionSelection(event) {
    this.status = quickShopState.submit
    this.quickShopVariant = event.detail.variant
  }

  setSwatchVariants() {
    if (!this.swatches || !this.variants.length) return []
    let nonColorOptions
    if (this.options?.length) {
      nonColorOptions = this.options.filter(
        (option) => !this.swatches.includes(option),
      )
    }

    const matches = this.swatches.reduce((accumulator, swatch) => {
      let match
      if (this.options?.length) {
        match = this.variants?.find((variant) => {
          for (const option of nonColorOptions) {
            if (!variant.options.includes(option)) return false
          }
          return variant.options.includes(swatch)
        })
      } else {
        match = this.variants?.find((variant) =>
          variant.options.includes(swatch),
        )
      }

      if (match) {
        const swatchHandle = handleize(swatch)
        const swatchColorConfig = window.__NOVA_GLOBAL__?.swatches[swatchHandle]
        match.swatchTitle = swatch
        match.swatchHandle = swatchHandle
        match.swatchBackground1 = 'transparent'
        match.swatchBackground2 = 'transparent'
        if (swatchColorConfig) {
          if (swatchColorConfig[0]) {
            match.swatchBackground1 = swatchColorConfig[0]
          }
          if (swatchColorConfig[1]) {
            match.swatchBackground2 = swatchColorConfig[1]
          }
        }
        accumulator.push(match)
      }
      return accumulator
    }, [])
    this.swatchVariants = matches
    this.activeColor = this.swatches[this.activeIndex || 0]
  }

  setSlidesForCarousel() {
    const container = this.$productCardContentRef.value

    if (this.isMobile || !container) {
      this.slidesForCarousel = 1
      return
    }

    const styles = getComputedStyle(container)
    const totalWidth =
      container.clientWidth -
      Number.parseFloat(styles.paddingLeft) -
      Number.parseFloat(styles.paddingRight)
    // Swatch size: var(--nova-product-color-swatch-size) = 25px over 768px screen width
    // Swatch gap: var(--nova-space-xs) = 10px over 768px screen width
    const slidesToShow = Math.floor(totalWidth / (25 + 10))
    this.slidesForCarousel = slidesToShow >= 1 ? slidesToShow : 1
  }

  handleSwatchSelected(event) {
    const { variantId, swatch, variantUrl } = event.detail
    this.quickShopVariant = null

    if (!variantId) return

    this.status = quickShopState.initial
    this.setActiveByVariantId(variantId)
    this.activeColor = swatch
    this.activeVariantId = String(variantId)
    this.activeVariantUrl = variantUrl || this.getVariantUrlById(variantId)
  }

  handleSwatchClick(event) {
    const { variantId } = event.target.dataset
    this.quickShopVariant = null

    if (!variantId) return

    this.status = quickShopState.initial
    this.setActiveByVariantId(variantId)
  }

  handleResize = () => {
    this.isMobile = window.innerWidth <= 768
    this.isHoverableDevice = window.matchMedia(
      '(hover: hover) and (pointer: fine) and (min-width: 1200px)'
    ).matches
    this.setSlidesForCarousel()
  }

  handleAddToWishlist(e) {
    const { variantId } = e.target.dataset

    if (!variantId) return

    alert(variantId)
  }

  handleMouseEnter() {
    if (this.isGiftCard) return
    if (!this.isHoverableDevice) return
    this.isHovered = true
  }

  handleMouseLeave() {
    this.isHovered = false
  }

  handleFeaturedProductMobileVariantSwitch(imageSrc) {
    const outerContainer = this.closest('.js-product-card-container')
    if (!outerContainer) return;

    const outerImage = outerContainer.querySelector('.js-product-card-outer-image');
    if (outerImage) {
      outerImage.src = imageSrc;
      const swiperEl = outerContainer.querySelector('.swiper');
      const swiper = swiperEl?.swiper;
      if (swiper) {
        const lastIndex = Math.max(0, swiper.slides.length - 1);
        swiper.slideTo(lastIndex, 300);
      }
    }

    const outerActiveUrl = outerContainer.querySelector('.js-product-card-outer-active-url');
    if (outerActiveUrl) {
      outerActiveUrl.setAttribute('href', this.activeUrl)
    }
  }

  setActiveIndex(index) {
    this.activeIndex = index
  }

  setActiveInventoryQty(index) {
    this.activeInventoryQty = this.inventoryQtys[index]
  }

  setActiveByVariantId(id, setPage = false) {
    const variantIndex = this.swatchVariants.findIndex(
      (variant) => variant.id === Number.parseInt(id),
    )

    if (variantIndex > -1) {
      this.setActiveIndex(variantIndex)
      this.setActiveInventoryQty(variantIndex)
    }

    if (setPage) {
      const pageNumber = this.paginatedSwatches.findIndex((page) =>
        page.some((variant) => variant.id === Number.parseInt(id)),
      )
      if (pageNumber > -1 && pageNumber !== this.activeSwatchPage) {
        this.setSwatchPage(pageNumber)
      }
    }
  }

  async handleAddToCartClick(e) {
    this.status = quickShopState.loading
    try {
      const variantId = e.target.dataset.variantId
      const properties = this.parseCallOutProperties(this.callOut)

      await addToCart({
        id: variantId,
        quantity: 1,
        properties: properties
      })
      this.status = quickShopState.confirm
    } catch (error) {
      this.status = quickShopState.error
    } finally {
      const quickShop = this.shadowRoot.querySelector(
        'nova-product-card-quick-shop',
      )
      quickShop.setActiveVariant(null)
      setTimeout(() => {
        this.status = quickShopState.initial
      }, 2000)
    }
  }

  parseCallOutProperties(callOut) {
    const properties = {}

    if (callOut && typeof callOut === 'string') {
      const pairs = callOut
        .split(',')
        .map(pair => pair.split(':').map(part => part.trim()))
        .filter(([key, value]) => key && value)

      for (const [key, value] of pairs) {
        properties[key] = value
      }
    }

    if (this.preOrderLabel) {
      properties._pre_order = this.preOrderLabel
    }

    return properties
  }

  handleNotifyMeClick() {
    console.log("notify me")
  }

  renderMedia() {
    return html`
      <div
        class="product-card__media ${classMap({
          'product-card__media--enable-hover': this.hasSecondaryMedia,
          'product-card__media--has-video': this.hasHoverVideo && this.isHoverableDevice
        })}"
        part="media-element"
        aria-hidden="true"
      >
        ${
          this.activeImage
            ? html`
            <div class="product-card__image product-card__image--primary">
              <img src=${this.activeImage} alt="" loading="lazy" />
            </div>`
            : window.productImagePlaceholder ? html`
            <div class="product-card__image product-card__image--primary">
              ${unsafeHTML(window.productImagePlaceholder)}
            </div>`
            : nothing
        }

        ${this.hasHoverVideo && this.isHoverableDevice
          ? html`
            <div class="product-card__image product-card__image--secondary product-card__video">
              <nova-card-video .videoObject=${this.activeHoverVideo} .description=${this.activeSecondaryImageAlt} loop></nova-card-video>
            </div>
          `
          : this.activeSecondaryImage ? html`
              <div class="product-card__image product-card__image--secondary">
                <img src=${this.activeSecondaryImage} alt="" loading="lazy" />
              </div>
          ` : nothing
        }
      </div>
    `
  }

  renderMediaControls() {
    return html`
      <div class="product-card__media-controls" part="quick-shop-container">
        ${this.renderBadges()}
        ${this.renderClubhouseBadge()}

        <nova-product-card-quick-shop
          product-options=${this.sizes}
          .active-variant=${this.swatchVariants[this.activeIndex]}
          .variants=${this.allVariantsBySwatch}
          .allVariants=${this.variants}
          .swatches=${this.swatches}
          .swatchesData=${this.swatchesData}
          .activeId=${this.activeId}
          .url=${this.getCollectionAwareUrl(this.url)}
          .activeUrl=${this.activeUrl}
          .callOut=${this.callOut}
          .preOrderLabel=${this.preOrderLabel}
          .inventoryQty=${this.activeInventoryQty}
          .variantUrls=${this.variantUrls}
          class=${classMap({ 'visually-hidden': !this.isHovered })}>
        </nova-product-card-quick-shop>

        ${this.showWishlist && this.isHovered ? this.renderWishlist() : nothing}
      </div>
    `
  }

  renderClubhouseBadge() {
    const currentSwatchData = this.swatchesData?.[this.activeIndex]
    const badgeUrlDesktop = currentSwatchData?.clubhouse_badge
    const badgeUrlMobile = currentSwatchData?.clubhouse_badge_mobile || badgeUrlDesktop

    if (!badgeUrlDesktop || badgeUrlDesktop === '') return nothing

    const badgeUrl = this.isMobile ? badgeUrlMobile : badgeUrlDesktop
    const badgeWidth = this.isMobile
      ? currentSwatchData?.clubhouse_badge_width_mobile
      : currentSwatchData?.clubhouse_badge_width_desktop

    return html`
      <div class="product-card__clubhouse-badge ${classMap({
        'product-card__clubhouse-badge--desktop': !this.isMobile,
        'product-card__clubhouse-badge--mobile': this.isMobile,
        'product-card__clubhouse-badge--visible': this.isMobile || this.isHovered
      })}" style=${styleMap({
        width: badgeWidth ? `${badgeWidth}px` : 'auto'
      })} ${ref(this.$clubhouseBadgeRef)}>
        <img src=${badgeUrl} alt="" aria-hidden="true" loading="lazy" />
      </div>
    `
  }

  renderBadges() {
    if (!this.badges || this.badges.length === 0) return null

    return html`
      <div class="badges">
        ${this.badges.map((badge) => this.renderBadge(badge))}
      </div>
    `
  }

  renderBadge(badge) {
    if (this.mode === 'featured-product') return;
    const badgeSanitize = this.sanitizeString(badge.label) || ''
    const newBadgeSanitize = this.sanitizeString(this.dataNewBadgeText) || ''
    if (this.collectionHandle === 'new-arrivals' && badgeSanitize === newBadgeSanitize) return null

    if (this.hiddenBadgeSet.has(badgeSanitize)) {
      return null;
    }

    const textColor = badge.text_color || '#000'
    const backgroundColor = badge.background_color || '#fff'

    return html`
      <span
        class="badges__item flex flex-align-center flex-justify-center"
        style=${styleMap({
          color: textColor,
          backgroundColor: backgroundColor,
        })}>
        ${String(badge.label).trim() || ''}
      </span>
    `
  }

  renderWishlist() {
    return html`
      <sl-button
        class="product-card__wishlist"
        variant="text"
        size="small"
        aria-label=${t('accessibility.add_to_wishlist', 'Add to wishlist')}
        data-variant-id=${this.activeId}
        @click=${this.handleAddToWishlist}>
        ${paths.heart}
        <span class="visually-hidden">${t('accessibility.add_to_wishlist', 'Add to wishlist')}</span>
      </sl-button>
    `
  }

  renderPrice() {
    return html`
      <span class="product-card__price caption flex">
        <div class="price ${classMap({
          'price--on-sale': this.activeCompareAtPrice,
        })}">
          <dl>
            <div class="price__regular">
              <dt>
                <span class="visually-hidden visually-hidden--inline">Regular price</span>
              </dt>
              <dd>
                <span class="price-item price-item--regular">${this.activePrice}</span>
              </dd>
            </div>

            ${
              this.activeCompareAtPrice
                ? html`
              <div class="price__sale ">
                <dt>
                  <span class="visually-hidden visually-hidden--inline">Sale price</span>
                </dt>
                <dd>
                  <span class="price-item price-item--sale">
                    ${this.activeCompareAtPrice}
                  </span>
                </dd>
                <dt class="price__compare">
                  <span class="visually-hidden visually-hidden--inline">Regular price</span>
                </dt>
                <dd class="price__compare">
                  <s class="price-item price-item--regular">
                    ${this.activePrice}
                  </s>
                </dd>
              </div>
              `
                : nothing
            }
          </dl>
        </div>
      </span>
    `
  }

  renderSwatch = (swatch) => {
    const swatchSanitize = this.sanitizeString(swatch)
    const variant = this.findVariantBySwatch(swatch)
    const stateClasses = {
      'product-card__swatch--active': variant?.id === this.activeId,
      'product-card__swatch--sold-out': !variant?.available,
    }

    return html`
      <button
        type="button"
        class="product-card__swatch ${classMap(stateClasses)}"
        style="background: ${__NOVA_GLOBAL__.swatches[swatchSanitize]};"
        data-variant-id=${variant?.id}
        aria-hidden="true"
        tabindex="-1"
        @click=${this.handleSwatchClick}>
      </button>
    `
  }

  renderProductSwatches() {
    const useCarousel =
      !this.isMobile &&
      this.slidesForCarousel !== 1 &&
      this.swatches?.length > this.slidesForCarousel

    return html`
      <sl-resize-observer @sl-resize=${this.handleResize}>
        <div class="product-card__swatch-container ${classMap({
          'product-card__swatch-container--carousel': useCarousel,
        })}">
          ${
            !useCarousel
              ? html`
              ${this.swatches?.map(this.renderSwatch)}

              ${
                this.swatches?.length > 5
                  ? html`<span class="product-card__swatch-container__trigger" aria-hidden="true">+ more</span>`
                  : ''
              }
            `
              : html`
              <sl-carousel
                slides-per-page=${this.slidesForCarousel}
                slides-per-move=${this.slidesForCarousel}
                style="--aspect-ratio: auto; --slide-gap: 10px;">
                ${this.swatches?.map(
                  (swatch) => html`
                    <sl-carousel-item> ${this.renderSwatch(swatch)}</sl-carousel-item>
                  `,
                )}
              </sl-carousel>
            `
          }
        </div>
      </sl-resize-observer>
    `
  }

  renderQuickShopCTAs() {
    if (
      !this.isHovered ||
      !this.quickShopVariant ||
      this.status === quickShopState.initial
    )
      return null

    if (
      this.status === quickShopState.submit ||
      this.status === quickShopState.loading
    ) {
      return html`
      ${this.quickShopVariant.available ? html`
        <sl-button
          variant="primary:ink"
          class="product-card__atc"
          data-variant-id=${this.quickShopVariant.id}
          ?loading=${this.status === quickShopState.loading}
          @click=${this.handleAddToCartClick}>
          Add to Bag
        </sl-button>
      ` : html`
        <sl-button
          variant="primary:ink"
          class="product-card__atc"
          data-variant-id=${this.quickShopVariant.id}
          ?loading=${this.status === quickShopState.loading}
          @click=${this.handleNotifyMeClick}>
          Notify Me
        </sl-button>
      `}
      `
    }

    if (this.status === quickShopState.confirm) {
      return html`
        <div class="product-card__atc product-card__atc--confirm">
          <span>Item Added to Bag!</span>
        </div>
      `
    }

    if (this.status === quickShopState.error) {
      return html`
        <div class="product-card__atc product-card__atc--confirm product-card__atc--error">
          <span>Something Went Wrong</span>
        </div>
      `
    }

    return null
  }

  renderCallOut() {
    if (!this.callOut?.trim()) {
      return nothing
    }

    const values = this.getCallOutValues()

    if (values.length === 0) {
      return nothing
    }

    return html`
      <div class="product-card__call-out">
        ${values.map(value => value)}
      </div>
    `
  }

  getCallOutValues() {
    const values = this.callOut
      .split(',')
      .map(pair => {
        const [key, value] = pair.split(':').map(s => s.trim());

        if (key === '_exchange-only' || key === '_final-sale') {
          if (
            this.mode === 'product-list-grid' ||
            this.mode === 'suggested-products' ||
            this.mode === 'predictive-search'
          ) return nothing;

          return value ? html`<span class="product-card__call-out-red">${value}</span>` : nothing;
        }

        if (key === '_call_out') {
          const temp = document.createElement('div');
          temp.innerHTML = value;
          const decodedHtml = temp.textContent;

          return decodedHtml ? html`<span style="color: ${this.callOutColor}">${decodedHtml}</span>` : nothing;
        }

        // default
        return value ? html`<span>${value}</span>` : nothing;
      })
      .filter((value) => value !== nothing);

    return values
  }

  viewAdditionalColorsLabel(len) {
    const count = len - 1
    if (count === 1) {
      return '1 Color'
    }
    return `${count} Colors`
  }

  render() {
    const featuredProductStyle = html`
    <style>
      sl-card::part(base) {
        border-radius: 0;
      }

      @media screen and (max-width: 767px) {
        .product-card__image {
          padding: 0 !important;
          display: none;
        }

        nova-product-card-quick-shop {
          z-index: 1;
        }
      }
    </style>
  `;

    return html`
      ${this.mode === 'featured-product' ? featuredProductStyle : nothing}
      <sl-card
        id="product-card-${this.id}"
        data-index="product-card-${this.activeIndex}"
        class="product-card ${this.class}"
        style="--padding: 0; --border-width: 0; --sl-shadow-x-small: 0;"
        part="card-element"
        @mouseenter=${this.handleMouseEnter}
        @mouseleave=${this.handleMouseLeave}
        @option-selected=${this.handleOptionSelection}>
        <div class="product-card__preview">
          <a href=${this.activeUrl} class="product-card__link">
            ${this.renderMedia()}
            <div class="product-card__content" ${ref(this.$productCardContentRef)} part="content-element">
              <header class="product-card__header" part="product-header-element">
                <div class="product-card__sub-header">
                  <h2 class="product-card__title" part="product-title-element">${this.productTitle}</h2>
                  ${this.swatches && this.swatches.length > 0 && this.activeColor !== '' ? html`
                    <div class="product-card__variant-info">
                      <span>in ${this.activeColor}</span>
                      ${this.swatches.length > 1 ? html`<span class="product-card__variant-color-count">+${this.viewAdditionalColorsLabel(this.swatches.length)}</span>` : nothing}
                    </div>
                  ` : nothing}
                </div>
                <nova-product-card-price
                  part="product-price-element"
                  class="product-card__price"
                  .price=${this.activePrice}
                  .compareAtPrice=${this.activeCompareAtPrice}>
                </nova-product-card-price>
              </header>
              ${this.renderCallOut()}
            </div>
          </a>
          ${this.renderMediaControls()}
        </div>
        <div class="product-card__info" part="product-info-container">
          <nova-product-card-swatches
            aria-hidden="true"
            .swatches=${this.swatches}
            .variants=${this.variants}
            .activeId=${this.activeId}
            .swatchesData=${this.swatchesData}
            .url=${this.getCollectionAwareUrl(this.url)}
            .variantUrls=${this.variantUrls}>
          </nova-product-card-swatches>
          ${this.renderQuickShopCTAs()}
        </div>
      </sl-card>
    `
  }

  static styles = [
    styles.reset,
    styles.a11y,
    styles.flex,
    styles.helpers,
    styles.typography,
    styles.skeleton,
    css`
      :host {
        display: flex;
        flex-direction: column;
        position: relative;
        container-type: inline-size;
        width: 100%;
        position: relative;
      }

      sl-card.product-card,
      sl-card::part(base) {
        position: relative;
      }

      .product-card__preview {
        position: relative;
      }

      .product-card__link {
        display: flex;
        flex-direction: column;
        color: inherit;
        text-decoration: none;
      }

      .product-card__content {
        padding: var(--nova-space-2xs) var(--nova-space-2xs) 0;
        display: flex;
        flex-direction: column;
        gap: var(--nova-space-2xs);
      }

      .product-card__header {
        display: flex;
        flex-direction: column;
        align-items: start;
        justify-content: space-between;
        gap: var(--nova-space-2xs);
        flex-direction: column;

        & .product-card__sub-header {
          width: 100%;
        }
      }

      @media screen and (min-width: 768px) {
        .product-card__header {
          gap: var(--nova-space-sm);
          flex-direction: row;
        }
      }

      .product-carousel__card .product-card__header {
        flex-direction: row;
      }

      .product-card__media {
        position: relative;
      }

      .product-card__media-controls {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        aspect-ratio: 0.75;
        z-index: 2;
        pointer-events: none;
      }

      .product-card__media-controls nova-product-card-quick-shop,
      .product-card__media-controls .product-card__wishlist {
        pointer-events: auto;
      }

      .product-card__media--has-video {
        overflow: hidden;
      }

      .product-card .badges {
        position: absolute;
        top: var(--nova-space-2xs);
        left: var(--nova-space-2xs);
      }

      nova-product-card-quick-shop {
        display: block;
        position: absolute;
        bottom: 0;
        left: 0;
        right: 32px;
      }

      .product-card:hover nova-product-card-quick-shop {
        display: block;
      }

      .product-card__image {
        width: 100%;
        aspect-ratio: 0.75;
        transition: opacity 0.3s ease-in-out;
        background: linear-gradient(150.38deg, rgb(222, 222, 218) 3.74%, rgba(222, 222, 218, 0.49) 63.73%, rgba(222, 222, 218, 0) 85.34%);
      }

      .product-card__image img {
        display: block;
        width: 100%;
        height: auto;
        aspect-ratio: 0.75;
        object-fit: cover;
        object-position: center;
      }

      .product-card__image--primary {
        opacity: 1;
      }

      .product-card__image--secondary {
        opacity: 0;
        position: absolute;
        top: 0;
        left: 0;
      }

      .product-card__image--secondary.product-card__video {
        height: 100%;
      }

      .product-card__video {
        display: block;
        width: 100%;
        height: 100%;
      }

      .product-card__video nova-card-video {
        display: block;
        width: 100%;
        height: 100%;
      }

      @media screen and (max-width: 1199px) {
        .product-card__image--secondary.product-card__video {
          opacity: 1;
        }
      }

      @container (min-width: 300px) {
        .product-card__preview:hover .product-card__media--enable-hover .product-card__image--primary {
          opacity: 0;
          position: absolute;
          top: 0;
          left: 0;
        }

        .product-card__preview:hover .product-card__media--enable-hover .product-card__image--secondary {
          opacity: 1;
          position: relative;
        }
      }

      .product-card__title {
        flex: 1;
        margin: 0;
      }

      .product-card__title,
      .product-card__variant-info,
      .product-card__variant-info span {
        font: var(--nova-body-font);

        & span {
          font: var(--nova-body-font);
        }
      }

      @media (max-width: 1199px) {
        .product-card__variant-info {
          display: none;
        }
      }

      .product-card__variant-color-count {
        display: none;

        @media (min-width: 768px) {
          display: inline-block;
          color: rgba(0, 0, 0, 0.55);
          margin-left: var(--nova-space-2xs);
        }
      }

      .product-card__price {
        flex: 0;
      }

      .product-card__price dd {
        margin: 0;
      }

      .product-card__info {
        overflow-y: hidden;
        position: relative;
        z-index: 2;
        padding: var(--nova-space-2xs) var(--nova-space-2xs) 0;
      }

      @media (min-width: 1200px) {
        .product-card__info {
          display: none;
        }
      }

      .product-card__info::-webkit-scrollbar {
        display: none;
      }

      @container (min-width: 300px) {
        .product-card__info {
          padding: var(--nova-space-2xs) var(--nova-space-2xs) 0;
        }
      }

      sl-carousel {
        width: 100%;
        height: 100%;
      }

      sl-carousel::part(base) {
        grid-template-rows: 1fr;
        grid-template-areas: '. slides .';
        gap: 3px;
      }

      sl-carousel::part(navigation-button) {
        padding: 0;
      }

      sl-carousel::part(scroll-container) {
        justify-items: flex-start;
      }

      .product-card__wishlist {
        position: absolute;
        right: var(--nova-space-xs);
        top: var(--nova-space-sm);
      }

      .product-card__wishlist svg {
        pointer-events: none;
      }

      .product-card__wishlist:hover svg {
        fill: var(--nova-brand-secondary);
      }

      .product-card__wishlist:active svg {
        fill: var(--nova-brand-ink);
      }

      .badges {
        display: flex;
        gap: var(--nova-space-2xs);
      }

      .badges__item {
        font: var(--nova-desktop-body-font);
        letter-spacing: normal;
        padding: 0 var(--nova-space-2xs);
        pointer-events: none;
        width: fit-content;
        min-height: var(--nova-space-md-1);

        @media screen and (min-width: 768px) {
          min-height: var(--nova-space-sm-1);
        }
      }

      .product-card__clubhouse-badge {
        position: absolute;
        z-index: 2;
        pointer-events: none;
      }

      .product-card__clubhouse-badge--mobile {
        top: var(--nova-space-xs);
        right: var(--nova-space-xs);
      }

      .product-card__clubhouse-badge--desktop {
        left: auto;
        right: 12px;
        opacity: 0;
      }

      .product-card__clubhouse-badge--desktop.product-card__clubhouse-badge--visible {
        opacity: 1;
      }

      .product-card__clubhouse-badge img {
        display: block;
        width: 100%;
        height: auto;
      }

      sl-button[variant='primary:ink'] {
        width: 100%;
      }

      sl-button[variant='primary:ink']::part(base) {
        transition: 0.15s;
      }
      sl-button[variant='primary:ink']::part(base):hover {
        background-color: transparent;
      }
      sl-button[variant='primary:ink'] {
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
      }

      sl-button[variant='primary:ink']::part(base) {
        background-color: var(--nova-brand-ink);
        border: none;
        color: white;
      }

      sl-button[variant='primary:ink']::part(base):hover {
        color: white;
        background-color: var(--nova-brand-primary);
      }

      sl-button[disabled]::part(base):hover {
        background-color: var(--nova-brand-neutral);
      }

      .product-card__atc {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        // margin-top: var(--nova-space-sm);
      }

      .product-card__atc span {
        line-height: 40px;
      }

      .product-card__atc--confirm {
        width: 100%;
        height: 40px;
        display: flex;
        justify-content: center;
        background-color: white;
        border-radius: var(--nova-button-radius);
        background-color: var(--nova-brand-secondary);
      }

      .product-card__atc--error {
        background-color: var(--nova-utility-error);
      }

      .product-card__call-out,
      .product-card__call-out span {
        font: var(--nova-desktop-body-font);
        color: var(--nova-call-out-color);
        margin: 0;
      }

      .product-card__call-out span:not(:last-child) {
        margin-right: var(--nova-space-3xs);
      }

      .product-card__image svg {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
      }

      .product-card__atc::part(base) {
        border-radius: 0;
      }

      @media (max-width: 1024px) {
        nova-product-card-quick-shop {
          display: none;
        }
      }
    `,
  ]

}

export { NovaProductCard }

if (!customElements.get('nova-product-card')) {
  customElements.define('nova-product-card', NovaProductCard)
}
